import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bot, Send, Sparkles, Loader2, Lock } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function MentorChat({ qid, userAnswer }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const start = async () => {
    setOpen(true);
    if (sessionId || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/mentor/start", { qid, user_answer: userAnswer });
      setSessionId(data.session_id);
      setMessages([{ role: "assistant", text: data.reply }]);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy || !sessionId) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/mentor/message", { session_id: sessionId, text });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    if (user && !user.vip) {
      return (
        <Link to="/pricing" data-testid={`mentor-locked-${qid}`}
          className="btn-tactile bg-su-secondary text-black border-su-secondaryShadow px-4 py-2 text-sm inline-flex items-center gap-2">
          <Lock className="w-4 h-4" /> Tanya AI Mentor — VIP
        </Link>
      );
    }
    return (
      <button data-testid={`mentor-open-${qid}`} onClick={start}
        className="btn-tactile bg-su-primary text-white border-su-primaryShadow px-4 py-2 text-sm inline-flex items-center gap-2">
        <Sparkles className="w-4 h-4" /> Tanya AI Mentor
      </button>
    );
  }

  return (
    <div className="rounded-2xl border-2 border-su-primary/30 bg-su-primary/5 overflow-hidden" data-testid={`mentor-chat-${qid}`}>
      <div className="flex items-center gap-2 px-4 py-3 bg-su-primary/10 text-su-primary font-heading font-black text-sm border-b-2 border-su-primary/20">
        <Bot className="w-4 h-4" /> AI Mentor · Gemini
      </div>
      <div className="max-h-72 overflow-auto p-4 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm font-medium whitespace-pre-line ${
              m.role === "user" ? "bg-su-primary text-white" : "bg-su-surface border-2 border-su-border text-su-text"
            }`}>{m.text}</div>
          </div>
        ))}
        {busy && (
          <div className="flex items-center gap-2 text-su-muted text-sm font-bold"><Loader2 className="w-4 h-4 animate-spin" /> Mentor sedang berpikir...</div>
        )}
        <div ref={endRef} />
      </div>
      <div className="flex items-center gap-2 p-3 border-t-2 border-su-primary/20 bg-su-surface">
        <input
          data-testid={`mentor-input-${qid}`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Tanya lebih lanjut..."
          className="flex-1 px-3 py-2 rounded-xl border-2 border-su-border bg-su-bg text-su-text text-sm font-medium outline-none focus:border-su-primary"
        />
        <button data-testid={`mentor-send-${qid}`} onClick={send} disabled={busy || !input.trim()}
          className="w-10 h-10 rounded-xl bg-su-primary text-white grid place-items-center disabled:opacity-50">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
